import { useLayoutEffect, useRef } from 'react';

import './AnnouncementBanner.scss';

import { Notification } from '@myome/ui';
import { ANNOUNCEMENTS, type Announcement } from './announcements';

const DISMISSED_ANNOUNCEMENT_PREFIX = 'dismissed-announcement:';

interface AnnouncementBannerProps {
  announcement: Announcement;
}

interface AnnouncementBannersProps {
  announcements?: readonly Announcement[];
}

export const AnnouncementBanner = ({ announcement }: AnnouncementBannerProps) => {
  if (!announcement.enabled) return null;

  return (
    <Notification
      dismissible
      dismissalKey={`${DISMISSED_ANNOUNCEMENT_PREFIX}${announcement.id}`}
      ariaLabel='Announcement'
      dismissLabel='Dismiss announcement'
      className='d-print-none'
    >
      {announcement.content}
    </Notification>
  );
};

export const AnnouncementBanners = ({ announcements = ANNOUNCEMENTS }: AnnouncementBannersProps) => {
  const enabledAnnouncements = announcements.filter(announcement => announcement.enabled);
  const bannersRef = useRef<HTMLDivElement>(null);

  // Expose the live height so fixed-position layout (patient detail sidebar) can offset past the banner.
  useLayoutEffect(() => {
    const banners = bannersRef.current;

    const setHeight = () => {
      document.body.style.setProperty('--announcement-banner-height', `${banners?.offsetHeight ?? 0}px`);
    };
    setHeight();

    const observer = new ResizeObserver(setHeight);
    if (banners) observer.observe(banners);

    return () => {
      observer.disconnect();
      document.body.style.removeProperty('--announcement-banner-height');
    };
  }, [enabledAnnouncements.length]);

  if (enabledAnnouncements.length === 0) return null;

  return (
    <div ref={bannersRef} className='announcement-banners d-print-none'>
      {enabledAnnouncements.map(announcement => (
        <AnnouncementBanner key={announcement.id} announcement={announcement} />
      ))}
    </div>
  );
};
